import { Schema, Struct } from "effect"
import { Rpc, RpcGroup } from "effect/rpc"
import { CommandRejection } from "../commands/index"
import { ModelProvider } from "../common"
import { MemoryDraft } from "../domain/knowledge"
import { Mind } from "../domain/org"
import {
  AgentId,
  CommandId,
  CredentialId,
  Handle,
  MemoryItemId,
  Millis,
  ProjectId,
  RepoId,
  RunnerId,
  Seq,
  Slug,
  UserId,
} from "../ids"
import { RunnerRecord, RunnerServes } from "../runner"
import { RunnerStatus } from "../state"

// #region workspace-rpc
/**
 * Workspace DO commands (people, through the Edge Worker; actor stamped from the session). Owner/admin-gated except
 * where noted. Each is decided against the workspace log and answered with a WorkspaceCommandResult. Secrets travel
 * only as `Redacted` in a command and are encrypted at rest before append: events carry metadata (CredentialAdded).
 */
export const WorkspaceCommand = Schema.TaggedUnion({
  // ---- runners (the Runners screen: pause, drain, revoke, rename) ----
  RunnerAdmin: {
    runnerId: RunnerId,
    action: Schema.TaggedUnion({
      pause: {}, // no new offers; running leases continue
      resume: {},
      drain: {}, // finish current leases, then pause
      revoke: {}, // token invalidated; live leases are revoked and requeued
      rename: { name: Schema.String },
    }),
  },
  // ---- model credentials ("API keys" in settings; user scope: the owner only) ----
  AddCredential: {
    credentialId: CredentialId,
    scope: Schema.TaggedUnion({ workspace: {}, user: { userId: UserId } }),
    provider: ModelProvider,
    kind: Schema.Literals(["api_key", "chatgpt_plan"]),
    label: Schema.String,
    secret: Schema.Redacted(Schema.String),
  },
  RemoveCredential: { credentialId: CredentialId },
  // ---- repos and projects ----
  RegisterRepo: { repoId: RepoId, remote: Schema.String, defaultBranch: Schema.String },
  RemoveRepo: { repoId: RepoId },
  /** Creates the Project DO (CreateProject there) and the project index row here. */
  RegisterProject: { projectId: ProjectId, slug: Slug, name: Schema.String },
  ArchiveProject: { projectId: ProjectId },
  // ---- the Mayor ----
  HireMayor: { agentId: AgentId, name: Schema.String, handle: Handle, mind: Mind },
  // ---- workspace memory (people write, agents propose in a project; locked = workspace legal rules) ----
  WriteMemory: {
    itemId: MemoryItemId,
    op: Schema.Literals(["create", "revise", "archive", "revoke"]),
    draft: Schema.NullOr(MemoryDraft),
    reason: Schema.NullOr(Schema.String),
  },
})
export type WorkspaceCommand = typeof WorkspaceCommand.Type

export const WorkspaceCommandResult = Schema.TaggedUnion({
  Applied: { firstSeq: Seq, lastSeq: Seq },
  Deduplicated: { firstSeq: Seq, lastSeq: Seq },
  Rejected: { rejection: CommandRejection }, // rejections write NO event
})

/** A runner as the Runners screen sees it: the record without its token hash. */
export const RunnerView = Schema.Struct({
  runner: RunnerRecord.mapFields(Struct.omit(["tokenHash"])),
  status: RunnerStatus,
})

/** The Runners screen and workspace settings. One socket per open workspace view; actor from the session. */
export class WorkspaceRpcs extends RpcGroup.make(
  Rpc.make("Dispatch", {
    payload: { commandId: CommandId, command: WorkspaceCommand },
    success: WorkspaceCommandResult,
  }),
  Rpc.make("ListRunners", {
    payload: {},
    success: Schema.Array(RunnerView),
  }),
  /** Live runner dots: the full list first, then one frame per change. */
  Rpc.make("WatchRunners", {
    payload: {},
    success: RunnerView,
    stream: true,
  }),
) {}
// #endregion workspace-rpc

// #region workspace-live
/**
 * Wave E (decision 0063): what every member of the workspace may see of any runner, pushed live on `/live`. No CLI
 * homes, account labels, profiles, effective config or leases: the owner reads those from `GET …/runners` (redacted
 * per viewer in the cloud). Decoders of this struct use the default excess-key handling; the protocol test checks that
 * a strict decode refuses machine detail such as `home` or `account`.
 */
export const RunnerPublicView = Schema.Struct({
  runnerId: RunnerId,
  name: Schema.String,
  os: Schema.String,
  arch: Schema.String,
  ownerUserId: UserId,
  status: Schema.Literals(["online", "offline"]),
  lastSeenAt: Schema.NullOr(Millis),
  serves: RunnerServes,
  version: Schema.String,
  /** Runs leased to it right now (a count only). */
  activeRuns: Schema.optionalKey(Schema.Number),
})
export type RunnerPublicView = typeof RunnerPublicView.Type

/** Pushed to every `/live` socket of the workspace when a runner's presence, record or `serves` changes. */
export const RunnerLiveEvent = Schema.TaggedUnion({
  RunnerUpdated: { runner: RunnerPublicView },
  RunnerRemoved: { runnerId: RunnerId },
})
export type RunnerLiveEvent = typeof RunnerLiveEvent.Type

/**
 * Served by the Workspace DO on `/api/workspaces/:workspaceId/live` (hibernatable WebSocket, members only).
 * `WatchRunners` is a persisted push RPC: the backlog is a `RunnerUpdated` per known runner, then live events.
 */
export class WorkspaceLiveRpcs extends RpcGroup.make(
  Rpc.make("ListRunners", { success: Schema.Array(RunnerPublicView) }),
  Rpc.make("WatchRunners", { success: RunnerLiveEvent, stream: true }),
) {}
// #endregion workspace-live
